import { DrawingGlobals, DrawingModes, VARS } from "../Global/variables";
import { performGlobalUndo, performGlobalRedo } from "../Global/UndoController";

// ✅ Tree copy/paste + ghost preview
import {
  copySelectedTree,
  pasteCopiedTree,
  stopTreePastePreview,
  isTreePastePreviewActive,
} from "../Trees/TreeCopyPaste";

// KeyEventListener handles keyboard shortcuts and events during drawing operations
class KeyEventListener {
  constructor(
    completeShape,
    currentDrawingMode,
    undo,
    redo,
    setDeleteConfirmationVisible,
    selectedIndices,
    cancelDrawing,
    removeRuler,
    cancelSegmentDrawing,
    updateDrawingMode,
    setDeletePanels
  ) {
    // Assign passed-in callbacks and states
    this.completeShape = completeShape;
    this.currentDrawingMode = currentDrawingMode;
    this.undo = undo;
    this.redo = redo;
    this.deleteConfirmation = setDeleteConfirmationVisible;
    this.selectedindices = selectedIndices;
    this.cancelDrawing = cancelDrawing;
    this.removeRuler = removeRuler;
    this.cancelSegmentDrawing = cancelSegmentDrawing;
    this.updateDrawingMode = updateDrawingMode;
    this.setDeletePanels = setDeletePanels;

    this.removeEventListener();

    this.keyDownHandler = this.keyDownHandler.bind(this);
    this.keyUpHandler = this.keyUpHandler.bind(this);

    window.addEventListener("keydown", this.keyDownHandler);
    window.addEventListener("keyup", this.keyUpHandler);
  }

  keyDownHandler(event) {
    // ✅ Don't steal shortcuts while typing
    const t = event.target;
    if (
      t &&
      (t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.isContentEditable)
    ) {
      return;
    }

    // ✅ Tree copy/paste (works in NONE mode too)
    // Ctrl/Cmd + C: copy selected tree + start ghost preview
    if ((event.ctrlKey || event.metaKey) && (event.key === "c" || event.key === "C")) {
      if (VARS?.selectedTree) {
        event.preventDefault();

        copySelectedTree(VARS.selectedTree); // ✅ this starts the ghost preview internally

        // Last-copy-wins semantics like your other systems (optional but consistent)
        VARS.copiedSegment = null;
        VARS.copiedObstacle = null;

        return;
      }
    }

    // Ctrl/Cmd + V: paste tree at ghost position (or current mouse)
    if ((event.ctrlKey || event.metaKey) && (event.key === "v" || event.key === "V")) {
      if (VARS?.copiedTree) {
        event.preventDefault();
        pasteCopiedTree();
        return;
      }
    }

    switch (event.key) {
      case "Enter":
        if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length >= 3
        ) {
          this.completeShape();
        }
        break;

      case "Escape":
        // ✅ First priority: cancel tree ghost preview if active
        if (isTreePastePreviewActive()) {
          stopTreePastePreview();
          return;
        }

        // Existing ESC behavior
        if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length > 0
        ) {
          this.undo();
        } else if (
          this.currentDrawingMode === DrawingModes.SEGMENT &&
          DrawingGlobals.intersectionPoints.length === 0
        ) {
          this.cancelSegmentDrawing();
        } else if (this.currentDrawingMode === DrawingModes.OBSTACLE) {
          this.cancelDrawing();
        } else if (this.currentDrawingMode === DrawingModes.RULER) {
          this.removeRuler();
        } else if (this.currentDrawingMode === DrawingModes.PANEL) {
          this.updateDrawingMode(DrawingModes.NONE);
        }
        break;

      case "z":
      case "Z":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          if (
            this.currentDrawingMode === DrawingModes.SEGMENT &&
            DrawingGlobals.intersectionPoints.length > 0
          ) {
            this.undo();
          } else {
            performGlobalUndo();
          }
        }
        break;

      case "y":
      case "Y":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          if (
            this.currentDrawingMode === DrawingModes.SEGMENT &&
            DrawingGlobals.intersectionPoints.length > 0
          ) {
            this.redo();
          } else {
            performGlobalRedo();
          }
        }
        break;

      case "Delete":
      case "Backspace":
        if (this.selectedindices.panelIndex !== null) {
          this.setDeletePanels(this.selectedindices.panelIndex);
          this.deleteConfirmation(true);
        }
        break;

      case "Control":
        // no-op
        break;

      default:
        break;
    }
  }

  removeEventListener() {
    window.removeEventListener("keydown", this.keyDownHandler);
    window.removeEventListener("keyup", this.keyUpHandler);
  }

  keyUpHandler(event) {}
}

export default KeyEventListener;
